import { logger } from "@/lib/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BillableEventTrigger {
  trial_site_id: string;
  patient_id: string;
  trial_id: string;
  event_type: 'enrollment' | 'visit' | 'milestone' | 'randomization';
  visit_id?: string;
  notes?: string;
}

export function useBillableEventTrigger() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const triggerMutation = useMutation({
    mutationFn: async (trigger: BillableEventTrigger) => {
      // 1. Find matching fee schedule for this trial and event type
      const { data: feeSchedules, error: feeError } = await supabase
        .from('protocol_fee_schedules')
        .select('*')
        .eq('trial_id', trigger.trial_id)
        .eq('is_active', true);

      if (feeError) throw feeError;

      // Map event type to fee category
      let category = 'visit';
      if (trigger.event_type === 'enrollment') category = 'milestone';
      if (trigger.event_type === 'randomization') category = 'milestone';
      if (trigger.event_type === 'milestone') category = 'milestone';

      const matchingSchedule = feeSchedules?.find(s => 
        s.fee_category === category ||
        s.fee_description?.toLowerCase().includes(trigger.event_type) ||
        s.fee_name?.toLowerCase().includes(trigger.event_type)
      );

      if (!matchingSchedule) {
        throw new Error(`No fee schedule found for ${trigger.event_type} in this trial`);
      }

      // 2. Create billable event
      const { data, error } = await supabase
        .from('billable_events')
        .insert({
          trial_site_id: trigger.trial_site_id,
          patient_id: trigger.patient_id,
          fee_schedule_id: matchingSchedule.id,
          event_date: new Date().toISOString(),
          unit_amount: matchingSchedule.amount,
          quantity: 1,
          total_amount: matchingSchedule.amount,
          currency: matchingSchedule.currency || 'USD',
          status: 'pending',
          visit_id: trigger.visit_id,
          notes: trigger.notes || `Auto-generated from ${trigger.event_type}`,
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billable-events'] });
      toast({
        title: "Billable Event Created",
        description: "Event has been added to billing queue",
      });
    },
    onError: (error: any) => {
      logger.error('Billable event trigger error:', error);
      toast({
        title: "Billing Error",
        description: error.message || "Failed to create billable event",
        variant: "destructive",
      });
    },
  });

  return {
    triggerBillableEvent: triggerMutation.mutate,
    isTriggering: triggerMutation.isPending,
  };
}
